import { Component, OnInit, OnDestroy } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ToastModule } from "primeng/toast";
import { LoadingSpinnerComponent } from "./core/components/loading-spinner/loading-spinner.component";
import { HeaderComponent } from "./core/components/header/header.component";
import { FooterComponent } from "./core/components/footer/footer.component";
import { environment } from "../environments/environment";
import { filter, Subject, takeUntil } from "rxjs";
import { SessionService } from "./core/services/session.service";
import clarity from "@microsoft/clarity";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ButtonModule,
    CardModule,
    ToastModule,
    LoadingSpinnerComponent,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  protected title = "QMC Angular 19";
  protected isNotAuthRoute = true;
  protected isWidgetsRoute = false;
  protected isIframeRoute = false;

  // Subject for cleanup on destroy
  private readonly destroy$ = new Subject<void>();

  // Array of route patterns that should have scrolling enabled
  private readonly scrollableRoutePatterns = [
    "/customer-portal",
    "/email-templates",
  ];

  constructor(
    private readonly router: Router,
    private readonly sessionService: SessionService,
  ) {
    // Subscribe to router events to determine if we're on an auth route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const currentUrl = this.router.url;

        // Check for standalone pages (auth pages + 404)
        // Traverse all activated routes to find isStandalonePage flag
        let isStandalonePage = false;
        let route = this.router.routerState.root;
        while (route.firstChild) {
          route = route.firstChild;
          if (route.snapshot.data?.["isStandalonePage"] === true) {
            isStandalonePage = true;
            break;
          }
        }

        this.isNotAuthRoute =
          currentUrl.includes("/login") ||
          currentUrl.includes("/register") ||
          currentUrl.includes("/forgot-password") ||
          currentUrl.includes("/unauthorized") ||
          isStandalonePage;

        // Check if we're on a widgets route
        this.isWidgetsRoute = currentUrl.includes("/widgets/dashboard");

        // Check if we're on a scrollable route
        this.isIframeRoute =
          currentUrl.includes("/widgets/dashboard/") &&
          this.scrollableRoutePatterns.some((pattern) =>
            currentUrl.includes(pattern),
          );
      });
  }

  ngOnInit(): void {
    // Initialize Microsoft Clarity
    clarity.init(environment.clarityId);

    // Load reCAPTCHA script if not already loaded
    if (!window.document.getElementById("recaptcha-script")) {
      const script = document.createElement("script");
      script.id = "recaptcha-script";
      script.src = `https://www.google.com/recaptcha/api.js?render=${environment.recaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
